import {
  CustomAttributeExtended,
  EditStorageFormData,
  StorageDefaultInfo,
  StorageFormData,
  StorageItemForEdit,
} from "./storage.types";
import { StorageItem } from "./Tables/types";

/**
 * Transforms an API custom_attributes array into flattened
 * `custom_attribute_<field_id>` form values.
 */
export const transformCustomAttributes = (
  data?: StorageDefaultInfo | StorageFormData
): Record<string, any> => {
  const from = (data as StorageDefaultInfo)?.custom_attributes;
  if (!from || from.length === 0) return {};
  const values: Record<string, any> = {};
  for (const attr of from) {
    const key = `custom_attribute_${attr.field_id}`;
    if (attr.editor_type === "tagBox") {
      try {
        values[key] = Array.isArray(attr.value)
          ? attr.value
          : JSON.parse((attr.value as string) || "[]");
      } catch (e) {
        values[key] = [];
      }
    } else if (attr.editor_type === "dateBox") {
      // The legacy custom-fields modal stored full ISO timestamps
      // ("2024-01-01T00:00:00-05:00"); the date input and validation schema
      // expect YYYY-MM-DD. Take the date part as stored — no timezone math,
      // so the calendar date never shifts.
      if (
        typeof attr.value === "string" &&
        /^\d{4}-\d{2}-\d{2}T/.test(attr.value)
      ) {
        values[key] = attr.value.slice(0, 10);
      } else {
        // Stored rows can hold "" for a cleared date; "" fails the optional
        // dateBox validation and DateInput offers no clear affordance for a
        // falsy value, so treat it as unset.
        values[key] = attr.value === "" ? null : attr.value;
      }
    } else {
      values[key] = attr.value;
    }
  }
  return values;
};

/**
 * Prepares a diff payload for PATCH: converts undefined to null and clears
 * emptied custom_attribute_<id> values; preserves custom_attributes arrays.
 */
export const modelDiffData = (
  diffData: EditStorageFormData
): EditStorageFormData => {
  const modeled: EditStorageFormData = {};
  Object.keys(diffData).forEach((key) => {
    const value: any = (diffData as any)[key];
    if (value === undefined) {
      (modeled as any)[key] = null;
    } else if (key === "custom_attributes" && Array.isArray(value)) {
      (modeled as any)[key] = value;
    } else if (key.startsWith("custom_attribute_")) {
      (modeled as any)[key] =
        value === undefined || value === "" ? null : value;
    } else {
      (modeled as any)[key] = value;
    }
  });
  return modeled;
};

const isEqual = (a: unknown, b: unknown): boolean => {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }
  return a === b;
};

/**
 * Shallow-diffs current form values against the original values; array values
 * are compared element-wise.
 */
export const diffFormData = (
  original: StorageFormData | undefined,
  current: StorageFormData
): EditStorageFormData => {
  if (!original) return { ...current };
  const diff: EditStorageFormData = {};
  Object.keys(current).forEach((key) => {
    if (!isEqual((current as any)[key], (original as any)[key])) {
      (diff as any)[key] = (current as any)[key];
    }
  });
  return diff;
};

/**
 * Extracts custom_attribute_ prefixed fields from a data object.
 * Returns the cleaned data (without custom attribute keys) and an array
 * of custom attributes suitable for the API.
 *
 * Does NOT mutate the input object.
 */
export const processCustomAttributesForAPI = (
  data: Record<string, unknown>
): {
  cleaned: Record<string, unknown>;
  customAttributes: Array<{ field_id: number; value: unknown }>;
} => {
  const customAttributes: Array<{ field_id: number; value: unknown }> = [];
  const cleaned: Record<string, unknown> = {};
  for (const key of Object.keys(data)) {
    if (key.startsWith("custom_attribute_")) {
      customAttributes.push({
        field_id: parseInt(key.replace("custom_attribute_", ""), 10),
        value: data[key],
      });
    } else {
      cleaned[key] = data[key];
    }
  }
  return { cleaned, customAttributes };
};

/**
 * Converts StorageItemForEdit to StorageFormData for use in DefaultStorageForm
 */
export const toStorageFormData = (
  item: StorageItemForEdit
): StorageFormData => ({
  storage_number: item.storage_number,
  type: item.type,
  serial_number: item.serial_number,
  capacity: item.capacity,
  capacity_unit: item.capacity_unit,
  model_name: item.model_name,
  model_number: item.model_number,
  make: item.make,
  encryption_key: item.encryption_key,
  is_general_storage: item.is_general_storage,
  notes: item.notes,
  case_id: item.case_id,
  custom_attributes: item.custom_attributes,
});

/**
 * The /storage/:storage_id route param holds either a numeric storage_id or
 * a uuid. Resolves it into the `{ uuid, storage_id }` pair used both as the
 * detail query key and the GET /storage/items query — shared so the edit
 * refetch in StorageOverview targets the exact key StorageDetailsPage reads.
 */
export const resolveStorageRouteParam = (routeParam?: string) => ({
  uuid: Number.isNaN(Number(routeParam)) ? routeParam : null,
  storage_id: Number.isNaN(Number(routeParam)) ? null : routeParam,
});

/**
 * Converts StorageItem to StorageItemForEdit format
 */
export const convertToEditableStorage = (
  details: StorageItem
): StorageItemForEdit | null => {
  if (!details.storage_id) return null;

  return {
    storage_id: details.storage_id,
    uuid: details.uuid,
    case_id: details.case_id,
    capacity: details.capacity,
    // Table rows type this as plain string; narrow at the boundary so the
    // form-facing types stay strict.
    capacity_unit: details.capacity_unit as StorageItemForEdit["capacity_unit"],
    storage_number: details.storage_number,
    type: details.type,
    serial_number: details.serial_number,
    model_name: details.model_name,
    model_number: details.model_number,
    make: details.make,
    encryption_key: details.encryption_key,
    is_general_storage: details.is_general_storage,
    notes: details.notes,
    custom_attributes:
      details.custom_attributes as unknown as CustomAttributeExtended[],
  };
};
